/**
 * React Flow custom-node-renderer voor `drawing.ListBox`-instances.
 *
 * Gedrag:
 *  - Toont een meertalige titel (uit concept.attributes.title) gevolgd
 *    door een alfabetisch gesorteerde lijst van items
 *  - Items zijn óf concepts (kind="concept") óf diagrammen
 *    (kind="diagram"). Diagrammen worden onderstreept gerenderd en
 *    klikbaar — klik opent/focust het diagram. Concept-items zijn
 *    pure tekst (no-op klik).
 *  - Drop-zone voor Explorer-items (concepts via EXISTING_CONCEPT_MIME,
 *    diagrammen via EXISTING_DIAGRAM_MIME). Duplicates worden silent
 *    geskipt.
 *  - Rechtsklik op een item → verwijder uit lijst.
 *
 * Per-instance data: leeft in viewState.listboxItems (zie types.ts).
 * Twee placements van dezelfde drawing.ListBox-instance op verschillende
 * diagrammen krijgen elk hun eigen lijst.
 */
import { Handle, NodeResizer, Position } from "@xyflow/react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { resolveBilingual } from "../../registry";
import { useSession } from "../../state/session";
import { useWorkspaceData } from "../../state/workspace";
import {
  EXISTING_CONCEPT_BULK_MIME,
  EXISTING_CONCEPT_MIME,
  EXISTING_DIAGRAM_MIME,
} from "../explorer/ConceptExplorer";

interface ListBoxData {
  conceptId: string;
  // Title is bilingual; rendering picks lang-appropriate.
  title?: { nl?: string; en?: string } | string;
  /** Per-instance items, gepushed via Canvas.tsx node-derivation. */
  items?: Array<{ id: string; kind: "concept" | "diagram" }>;
  /** Optional fill/border colors (from nodeStyle). */
  fillColor?: string;
  strokeColor?: string;
  fontColor?: string;
}

export function ListBoxNode({
  id,
  data,
  selected,
  width,
  height,
}: {
  id: string;
  data: ListBoxData;
  selected?: boolean;
  width?: number;
  height?: number;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const workspaceId = useSession((s) => s.workspaceId);
  const branchId = useSession((s) => s.branchId);
  // Alleen kijken: diagrammen openen mag; items erbij, eraf en vergroten niet.
  const readOnly = useSession((s) => s.readOnly);
  // revision-dependency zorgt dat we re-renderen op concept/diagram-naam-
  // changes elders, zodat de lijst altijd actuele tekst toont.
  const revision = useWorkspaceData((s) => s.revision);
  void revision;
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  void hoveredIdx;

  const items = data.items ?? [];
  const ws = useWorkspaceData.getState();

  // Resolveer item-namen + sorteer alfabetisch op active-language.
  const resolvedItems = useMemo(() => {
    const out: Array<{
      id: string;
      kind: "concept" | "diagram";
      name: string;
      missing: boolean;
    }> = [];
    for (const it of items) {
      let name = "";
      let missing = false;
      if (it.kind === "concept") {
        const c = ws.store.getConcept(it.id);
        if (c) name = resolveBilingual(c.name, lang);
        else missing = true;
      } else {
        const d = ws.store.getDiagram(it.id);
        if (d) name = resolveBilingual(d.name, lang);
        else missing = true;
      }
      out.push({ id: it.id, kind: it.kind, name: name || it.id, missing });
    }
    out.sort((a, b) => a.name.localeCompare(b.name, lang));
    return out;
    // ws is captured by reference; revision in deps triggers re-derivation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, lang, revision]);

  const titleText = (() => {
    const title = data.title;
    if (!title) return "";
    if (typeof title === "string") return title;
    return resolveBilingual(title, lang);
  })();

  const addItem = (newItem: { id: string; kind: "concept" | "diagram" }) => {
    if (!workspaceId || !branchId) return;
    if (useSession.getState().readOnly) return;
    // Silent skip bij duplicate.
    if (items.some((it) => it.id === newItem.id && it.kind === newItem.kind)) return;
    const next = [...items, newItem];
    ws.setListboxItemsOnCanvas(workspaceId, branchId, id, next);
  };

  const removeItem = (idx: number) => {
    if (!workspaceId || !branchId) return;
    if (useSession.getState().readOnly) return;
    const target = resolvedItems[idx];
    if (!target) return;
    const next = items.filter((it) => !(it.id === target.id && it.kind === target.kind));
    ws.setListboxItemsOnCanvas(workspaceId, branchId, id, next);
  };

  const onDiagramClick = (diagramId: string) => {
    if (!workspaceId) return;
    // openDiagram zorgt dat 't diagram in de openDiagramIds-lijst komt
    // (als nieuw) en zet 'm als activeDiagramId (= krijgt focus). Bestond
    // 't al → blijft 't in de lijst, activeDiagramId switcht.
    ws.openDiagram(workspaceId, diagramId);
  };

  return (
    <>
      <NodeResizer
        isVisible={!!selected && !readOnly}
        minWidth={120}
        minHeight={80}
        lineStyle={{ borderColor: data.strokeColor ?? "#94a3b8" }}
        handleStyle={{ width: 8, height: 8, borderRadius: 2 }}
      />
      {/* Vier React-Flow handles op de zijden voor edge-connectivity. */}
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
      <Handle type="target" position={Position.Left} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Right} style={{ opacity: 0 }} />
      {/* biome-ignore lint/a11y/noStaticElementInteractions: React-Flow node; selectie via React-Flow, geen per-element tab-stop */}
      <div
        className="flex h-full w-full flex-col rounded"
        style={{
          width: width ?? "100%",
          height: height ?? "100%",
          background: data.fillColor ?? "#ffffff",
          border: `1.5px solid ${data.strokeColor ?? "#94a3b8"}`,
          color: data.fontColor ?? "#0F172A",
          overflow: "hidden",
        }}
        onDragOver={(e) => {
          const hasConcept =
            e.dataTransfer.types.includes(EXISTING_CONCEPT_MIME) ||
            e.dataTransfer.types.includes(EXISTING_CONCEPT_BULK_MIME) ||
            e.dataTransfer.types.includes(EXISTING_DIAGRAM_MIME);
          if (!hasConcept) return;
          e.preventDefault();
          e.stopPropagation();
          e.dataTransfer.dropEffect = "copy";
        }}
        onDrop={(e) => {
          // stopPropagation zodat Canvas.onDrop niet ook nog een
          // standaard-placement doet (anders zou 't object dubbel
          // geplaatst worden: bij de ListBox EN ergens los op canvas).
          const conceptId = e.dataTransfer.getData(EXISTING_CONCEPT_MIME);
          const conceptBulkJson = e.dataTransfer.getData(EXISTING_CONCEPT_BULK_MIME);
          const diagramId = e.dataTransfer.getData(EXISTING_DIAGRAM_MIME);
          if (conceptId) {
            e.preventDefault();
            e.stopPropagation();
            addItem({ id: conceptId, kind: "concept" });
            return;
          }
          if (conceptBulkJson) {
            e.preventDefault();
            e.stopPropagation();
            try {
              const ids = JSON.parse(conceptBulkJson) as string[];
              for (const cid of ids) addItem({ id: cid, kind: "concept" });
            } catch {
              /* silent fail on malformed JSON */
            }
            return;
          }
          if (diagramId) {
            e.preventDefault();
            e.stopPropagation();
            addItem({ id: diagramId, kind: "diagram" });
            return;
          }
        }}
      >
        {/* Titel — wordt gerendeerd zelfs als leeg, geeft de gebruiker
            een visuele hint om iets te typen via het AttributePanel. */}
        <div
          className="border-slate-300 border-b px-2 py-1 font-semibold text-sm"
          style={{ background: "rgba(0,0,0,0.04)" }}
        >
          {titleText || (
            <span className="text-slate-400 italic">{t("canvas.listBox.noTitle")}</span>
          )}
        </div>
        {/* Lijst. nodrag op de items zodat klik/right-click niet de hele
            node start te draggen. */}
        <ul className="nodrag flex-1 overflow-auto px-2 py-1 text-xs">
          {resolvedItems.length === 0 && (
            <li className="py-1 text-slate-400 italic">{t("canvas.listBox.empty")}</li>
          )}
          {resolvedItems.map((it, idx) => (
            <li
              key={`${it.kind}:${it.id}`}
              className="flex items-center gap-1 py-0.5 hover:bg-slate-100"
              onMouseEnter={() => setHoveredIdx(idx)}
              onMouseLeave={() => setHoveredIdx(null)}
              onContextMenu={(e) => {
                if (readOnly) return;
                // Rechtermuisklik → verwijder. We tonen geen aparte
                // menu-popup; één-actie context met directe wissing
                // matcht het "BMC-style verwijderen"-pattern.
                e.preventDefault();
                e.stopPropagation();
                removeItem(idx);
              }}
            >
              {it.kind === "diagram" ? (
                <button
                  type="button"
                  className="cursor-pointer truncate text-left underline hover:text-blue-700"
                  style={it.missing ? { color: "#dc2626" } : undefined}
                  onClick={(ev) => {
                    ev.stopPropagation();
                    onDiagramClick(it.id);
                  }}
                  title={t("canvas.listBox.openDiagram")}
                >
                  {it.name}
                </button>
              ) : (
                <span
                  className="truncate"
                  style={it.missing ? { color: "#dc2626" } : undefined}
                  title={t("canvas.listBox.removeHint")}
                >
                  {it.name}
                </span>
              )}
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}
